/*
 * Copyright © 2020-2026 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 获取批量编辑水印入口兼容性校验实验版本
 */

import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { useAppSelector } from '../../../../app/hooks'
import {
  BATCH_WATERMARK_ENTRY_VALIDATION_CONFIG_KEY,
  BATCH_WATERMARK_ENTRY_VALIDATION_QUERY_KEY,
  resolveBatchWatermarkEntryValidationVariant
} from '../utils/batchWatermarkEntryValidationExperiment'
import type { BatchWatermarkEntryValidationVariant } from '../utils/batchWatermarkEntryValidationExperiment'
import { parseBatchWatermarkExperimentRolloutPercentage } from '../utils/batchWatermarkExperimentConfig'

/**
 * 获取当前团队稳定命中的入口兼容性校验版本。
 *
 * 生产环境始终按团队短 ID 分桶；非生产环境允许通过 query 快速验收。
 *
 * @returns 当前入口校验实验版本
 */
export const useBatchWatermarkEntryValidationVariant =
  (): BatchWatermarkEntryValidationVariant => {
    const teamShortId = useAppSelector((state) => state.group.invite_id)
    const remoteConfigValue: unknown = useAppSelector(
      (state) =>
        state.main.config?.[BATCH_WATERMARK_ENTRY_VALIDATION_CONFIG_KEY]
    )
    const { search } = useLocation()

    return useMemo(() => {
      const overrideVariant = new URLSearchParams(search).get(
        BATCH_WATERMARK_ENTRY_VALIDATION_QUERY_KEY
      )
      const isProductionBuild = __BUILD_ENV__ === 'prod'
      const rolloutPercentage = parseBatchWatermarkExperimentRolloutPercentage(
        remoteConfigValue,
        isProductionBuild ? 'production' : 'test'
      )

      return resolveBatchWatermarkEntryValidationVariant(teamShortId, {
        rolloutPercentage,
        overrideVariant,
        allowOverride: !isProductionBuild
      })
    }, [remoteConfigValue, search, teamShortId])
  }
